import { useTranslation } from '@bcba/react-hooks';
import { SectionError } from '../../DigitalDossierSection/components/SectionError';
import {
  Accordion,
  AccordionItem,
  Badge,
  Button,
  Center,
  DropzoneReadonly,
  EmptyState,
  FiAlertCircle,
  FiEdit,
  FiFileText,
  FiPlusCircle,
  FiSearch,
  FiTrash2,
  FiTool,
  Flex,
  Grid,
  GridItem,
  ReadOnlyField,
  Spinner,
  Stack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@agrotoken-tech/ui';
import { FormSection } from '../../DigitalDossierSection/components/FormSection';
import { UpsertProductiveFacility } from './UpsertProductiveFacility';
import { downloadDocument } from '../../../../utils/downloadDocument.utils';
import { formatDate } from '@bcba/common-utils';
import { UpsertProductiveAffidavit } from './UpsertProductiveAffidavit';
import { DeleteProductiveFacilityModal } from './DeleteProductiveFacilityModal';
import { DeleteProductiveAffidavitModal } from './DeleteProductiveAffidavitModal';
import { useProductiveFacilities } from './useProductiveFacilities';
import { validationStatusBadgeColors } from '@bcba/frontend-utils';
import { FacilityStatusAlert } from './FacilityStatusAlert';

interface Props {
  refetchStatusSummary: () => void;
}

export const ProductiveFacilities = ({ refetchStatusSummary }: Props) => {
  const { t } = useTranslation();

  const {
    data,
    isPending,
    isError,
    showUpsertFacilityPage,
    selectedFacility,
    selectedAffidavit,
    showUpsertAffidavitPage,
    onSuccessUpsertFacility,
    onCancelUpsertFacility,
    onSuccessUpsertAffidavit,
    onCancelUpsertAffidavit,
    onAddFacility,
    handleOnEditFacility,
    isOpenDeleteFacility,
    onCloseDeleteFacility,
    handleOnDeleteFacility,
    onSuccessDeleteFacility,
    defaultFacilityIndex,
    handleOnAddAffidavit,
    handleOnEditAffidavit,
    isOpenDeleteAffidavit,
    onCloseDeleteAffidavit,
    handleOnDeleteAffidavit,
    onSuccessDeleteAffidavit,
    defaultAffidavitIndex,
    handleRequestApproval,
    isPendingRequestApproval,
    showMobileAccordionItemActions,
  } = useProductiveFacilities({ refetchStatusSummary });

  if (isError)
    return (
      <SectionError
        message={t('explorer.productiveFacilities.getInfo.error')}
      />
    );

  if (isPending || !data)
    return (
      <Center mt="8">
        <Spinner />
      </Center>
    );

  if (showUpsertFacilityPage)
    return (
      <UpsertProductiveFacility
        onSuccess={onSuccessUpsertFacility}
        onCancel={onCancelUpsertFacility}
        selectedFacility={selectedFacility}
      />
    );

  if (showUpsertAffidavitPage)
    return (
      <UpsertProductiveAffidavit
        onSuccess={onSuccessUpsertAffidavit}
        onCancel={onCancelUpsertAffidavit}
        facilityUuid={selectedFacility?.uuid ?? ''}
        selectedAffidavit={selectedAffidavit}
      />
    );

  return (
    <>
      <FormSection
        title={t('common.establishments')}
        action={
          <Button
            variant={'linkPrimary'}
            leftIcon={<FiPlusCircle />}
            onClick={onAddFacility}
            size={'sm'}
          >
            {t('explorer.productiveFacilities.addFacility')}
          </Button>
        }
      >
        {data.length ? (
          <Accordion allowToggle defaultIndex={defaultFacilityIndex}>
            {data.map((facility, facilityIndex) => {
              const showRequestApproval =
                facility.status !== 'PENDING_APPROVAL' &&
                facility.status !== 'APPROVED';
              const shouldDisableRequestApproval = !Boolean(
                facility.productiveAffidavits?.length,
              );

              const geolocationDocument = facility.geolocationDocument;

              return (
                <AccordionItem
                  key={facility.uuid}
                  title={t('productiveFacilities.accordion.title').replace(
                    '{{n}}',
                    String(facilityIndex + 1),
                  )}
                  Badge={() => (
                    <Badge
                      colorScheme={validationStatusBadgeColors[facility.status]}
                    >
                      {t(`validationStatus.${facility.status}`)}
                    </Badge>
                  )}
                  RightActionButton={
                    showRequestApproval
                      ? () => (
                          <Button
                            variant="primary"
                            isDisabled={shouldDisableRequestApproval}
                            onClick={() => handleRequestApproval(facility.uuid)}
                            isLoading={isPendingRequestApproval}
                          >
                            {t('common.requestApproval')}
                          </Button>
                        )
                      : undefined
                  }
                  showActions={true}
                  HeadButton={() =>
                    showMobileAccordionItemActions ? (
                      <Flex gap={2} mr={2}>
                        <Menu>
                          <MenuButton
                            as={Button}
                            variant="link"
                            rightIcon={<FiTool />}
                          />
                          <MenuList>
                            <MenuItem
                              icon={<FiEdit />}
                              onClick={() =>
                                handleOnEditFacility(facility, facilityIndex)
                              }
                            >
                              {t('common.edit')}
                            </MenuItem>
                            <MenuItem
                              icon={<FiTrash2 />}
                              onClick={() => handleOnDeleteFacility(facility)}
                            >
                              {t('common.delete')}
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </Flex>
                    ) : (
                      <Flex gap={2} mr={2}>
                        <Button
                          variant={'linkPrimary'}
                          leftIcon={<FiEdit />}
                          onClick={() =>
                            handleOnEditFacility(facility, facilityIndex)
                          }
                          size={'sm'}
                        >
                          {t('common.edit')}
                        </Button>
                        <Button
                          variant={'linkDestructive'}
                          leftIcon={<FiTrash2 />}
                          onClick={() => handleOnDeleteFacility(facility)}
                          size={'sm'}
                        >
                          {t('common.delete')}
                        </Button>
                      </Flex>
                    )
                  }
                >
                  <Stack gap={4}>
                    <FacilityStatusAlert facility={facility} />
                    <Grid templateColumns={{ md: 'repeat(2, 1fr)' }} gap={6}>
                      <ReadOnlyField
                        label={t(
                          'productiveFacilities.upsert.establishmentName.label',
                        )}
                        value={facility.name}
                      />
                      <ReadOnlyField
                        label={t('productiveFacilities.upsert.ownership.label')}
                        value={t(`ownershipType.${facility.ownershipType}`)}
                      />
                      <ReadOnlyField
                        label={t('productiveFacilities.upsert.province.label')}
                        value={facility.province.name}
                      />
                      <ReadOnlyField
                        label={t('productiveFacilities.upsert.locality.label')}
                        value={facility.locality.name}
                      />
                      {geolocationDocument ? (
                        <GridItem colSpan={{ base: 1, md: 2 }}>
                          <DropzoneReadonly
                            label={t(
                              'productiveFacilities.upsert.geolocationDocument.label',
                            )}
                            fileName={geolocationDocument.originalFileName}
                            onClick={() =>
                              downloadDocument(geolocationDocument.uuid)
                            }
                            leftIcon={FiFileText}
                          />
                        </GridItem>
                      ) : null}
                      <GridItem colSpan={{ base: 1, md: 2 }}>
                        <FormSection
                          title={t('common.affidavits')}
                          action={
                            <Button
                              variant={'linkPrimary'}
                              leftIcon={<FiPlusCircle />}
                              onClick={() =>
                                handleOnAddAffidavit(facility, facilityIndex)
                              }
                              size={'sm'}
                            >
                              {t('explorer.productiveFacilities.addAffidavit')}
                            </Button>
                          }
                        >
                          {facility.productiveAffidavits?.length ? (
                            <Accordion
                              allowToggle
                              defaultIndex={defaultAffidavitIndex}
                            >
                              {facility.productiveAffidavits.map(
                                (affidavit, affidavitIndex) => (
                                  <AccordionItem
                                    title={t(
                                      'productiveFacilities.upsert.newAffidavit.title',
                                    ).replace(
                                      '{{n}}',
                                      String(affidavitIndex + 1),
                                    )}
                                    HeadButton={() =>
                                      showMobileAccordionItemActions ? (
                                        <Flex gap={2} mr={2}>
                                          <Menu>
                                            <MenuButton
                                              as={Button}
                                              variant="link"
                                              rightIcon={<FiTool />}
                                            />
                                            <MenuList>
                                              <MenuItem
                                                icon={<FiEdit />}
                                                onClick={() =>
                                                  handleOnEditAffidavit(
                                                    facility,
                                                    affidavit,
                                                    affidavitIndex,
                                                    facilityIndex,
                                                  )
                                                }
                                              >
                                                {t('common.edit')}
                                              </MenuItem>
                                              <MenuItem
                                                icon={<FiTrash2 />}
                                                onClick={() =>
                                                  handleOnDeleteAffidavit(
                                                    affidavit,
                                                  )
                                                }
                                              >
                                                {t('common.delete')}
                                              </MenuItem>
                                            </MenuList>
                                          </Menu>
                                        </Flex>
                                      ) : (
                                        <Flex gap={2} mr={2}>
                                          <Button
                                            variant={'linkPrimary'}
                                            leftIcon={<FiEdit />}
                                            onClick={() =>
                                              handleOnEditAffidavit(
                                                facility,
                                                affidavit,
                                                affidavitIndex,
                                                facilityIndex,
                                              )
                                            }
                                            size={'sm'}
                                          >
                                            {t('common.edit')}
                                          </Button>
                                          <Button
                                            variant={'linkDestructive'}
                                            leftIcon={<FiTrash2 />}
                                            onClick={() =>
                                              handleOnDeleteAffidavit(affidavit)
                                            }
                                            size={'sm'}
                                          >
                                            {t('common.delete')}
                                          </Button>
                                        </Flex>
                                      )
                                    }
                                  >
                                    <Grid
                                      templateColumns={{ md: 'repeat(2, 1fr)' }}
                                      gap={6}
                                    >
                                      <ReadOnlyField
                                        label={t(
                                          'productiveFacilities.upsert.productiveHectares.label',
                                        )}
                                        value={String(affidavit.hectares)}
                                      />
                                      <ReadOnlyField
                                        label={t(
                                          'productiveFacilities.upsert.exploitationType.label',
                                        )}
                                        value={t(
                                          `exploitationType.${affidavit.exploitationType}`,
                                        )}
                                      />
                                      <ReadOnlyField
                                        label={t(
                                          'productiveFacilities.upsert.startDate.label',
                                        )}
                                        value={formatDate(affidavit.startDate)}
                                      />
                                      <ReadOnlyField
                                        label={t(
                                          'productiveFacilities.upsert.endDate.label',
                                        )}
                                        value={formatDate(affidavit.endDate)}
                                      />
                                      <ReadOnlyField
                                        label={t(
                                          'productiveFacilities.upsert.renspa.label',
                                        )}
                                        value={affidavit.renspa}
                                      />
                                      <ReadOnlyField
                                        label={t(
                                          'productiveFacilities.upsert.currentRenspa.label',
                                        )}
                                        value={
                                          affidavit.renspaIsCurrent === true
                                            ? t('common.yes')
                                            : affidavit.renspaIsCurrent ===
                                                false
                                              ? t('common.no')
                                              : '-'
                                        }
                                      />
                                    </Grid>
                                  </AccordionItem>
                                ),
                              )}
                            </Accordion>
                          ) : (
                            <EmptyState
                              size="sm"
                              title={t(
                                'productiveFacilities.affidavitsEmptyState.title',
                              )}
                              description={t(
                                'productiveFacilities.affidavitsEmptyState.description',
                              )}
                              icon={FiAlertCircle}
                            />
                          )}
                        </FormSection>
                      </GridItem>
                    </Grid>
                  </Stack>
                </AccordionItem>
              );
            })}
          </Accordion>
        ) : (
          <EmptyState
            title={t('explorer.productiveFacilities.emptyState.title')}
            description={t(
              'explorer.productiveFacilities.emptyState.description',
            )}
            size="md"
            icon={FiSearch}
          />
        )}
      </FormSection>
      <DeleteProductiveFacilityModal
        facilityUuid={selectedFacility?.uuid ?? ''}
        isOpen={isOpenDeleteFacility}
        onClose={onCloseDeleteFacility}
        onSuccess={onSuccessDeleteFacility}
      />
      <DeleteProductiveAffidavitModal
        affidavitUuid={selectedAffidavit?.uuid ?? ''}
        isOpen={isOpenDeleteAffidavit}
        onClose={onCloseDeleteAffidavit}
        onSuccess={onSuccessDeleteAffidavit}
      />
    </>
  );
};
